import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  Dispatch,
  DispatchDuration,
  DispatchSearchParams,
  RosterDetailType,
  RosterFilter,
  RosterMember,
} from "../../common/Dispatch";
import { Link, NavLink, useParams, useNavigate } from "react-router-dom";
import { useCreateEvaluationFromDispatchMutation } from "../../redux/api/evaluation";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Switch,
  Dialog,
  TableSortLabel,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import moment from "moment";
import {
  JobOrder,
  JobRating,
  JobRatingLabel,
  LicensedJobType,
  Publish,
  ReliefReason,
  ReliefReasonLabel,
} from "../../common/JobOrder";
import {
  useGetPortsQuery,
  useGetVesselQuery,
  useUpdateVesselMutation,
} from "../../redux/api/vessel";
import { useGetJobOrdersQuery } from "../../redux/api/jobOrder";
import {
  useGetDispatchesQuery,
  useSaveDispatchesMutation,
} from "../../redux/api/dispatch";
import { useSaveJobOrdersMutation } from "../../redux/api/jobOrder";
import convertDateToString from "../../util/ConvertDateToString";
import MarinerProfileAutocomplete from "../marinerProfile/MarinerProfileAutocomplete";
import {
  DisabledPorts,
  PortMasterEnum,
  PortMasterLabel,
} from "../../common/Port";
import { jobOrderSearchParams } from "../../common/JobOrder";
import { usePrompt } from "../../util/usePrompt";
import { useSelector } from "react-redux";
import {
  selectCurrentCaptain,
  selectCurrentDispatch,
  selectCurrentUser,
} from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import Button from "../editor/toolbar/Button";
import { MarinerUnion } from "../../common/MarinerProfile";
import { toast } from "react-toastify";

const RosterChangeForm = (props: {
  parentCallback: Function;
  rosterStatusCallback: Function;
  changedRowsWithError: number[];
  // pass in state variables from the parent to keep track of when the form is submitted
  formSubmitted: boolean;
  setFormSubmitted: (value: boolean) => void;
  // pass in state variable from the parent to keep track of EXTRA editing
  editingExtra: boolean;
  setEditingExtra: (value: boolean) => void;
  // pass in state variable from the parent to keep track of TRAINING PERMISSION editing
  editingTrainingPermission: boolean;
  setEditingTrainingPermission: (value: boolean) => void;
  // variables to control filter
  filter?: RosterFilter;
}) => {
  const user = useSelector(selectCurrentUser);
  const isCaptain = useSelector(selectCurrentCaptain);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES;
  const noProfileLink =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES ||
    ((user?.userGroup as UserGroup) === UserGroup.MARINERS && !isCaptain);
  const isExecutive =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS;
  const isCrewing =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_MANAGERS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_SPECIALISTS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EVAL_SPECIALISTS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES_ADMINS;
  const navigate = useNavigate();
  const [createEvalFromDispatch] = useCreateEvaluationFromDispatchMutation();
  const defaultRoster: RosterMember[] = [];
  const [roster, setRoster] = useState<RosterMember[]>(defaultRoster);
  const [changedRows, setChangedRows] = useState<number[]>([]);
  const [changedDispatches, setChangedDispatches] = useState<number[]>([]);
  const [rosUpdated, setRosUpdated] = useState(false);
  const [changeToFOS, setChangeToFOS] = useState(false);
  const [changeToROS, setChangeToROS] = useState(false);
  const [rosExtra, setRosExtra] = useState<{ [rating: string]: number }>({});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<
    "asc" | "desc" | undefined
  >(undefined);
  const replaceableRatings = [
    JobRating._Master,
    JobRating._CM,
    JobRating._2M,
    JobRating._3M,
    JobRating._CE,
    JobRating._1AE,
    JobRating._2AE,
    JobRating._3AE,
  ];
  
  const currentDispatch: Dispatch = useSelector(selectCurrentDispatch);
  const isNonArcCaptain = currentDispatch?.vessel?.name ? !currentDispatch.vessel?.name.startsWith('ARC') && isCaptain : false;
  const [evalClick, setEvalClick] = useState<boolean>(false);

  const { id } = useParams();
  const {
    data: vessel,
    isLoading: isVesselLoading,
    isFetching: isVesselFetching,
  } = useGetVesselQuery(id !== undefined && id !== "new" ? id : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const [vesselROS, setVesselROS] = useState(false);

  useEffect(() => {
    if (!isVesselLoading && !isVesselFetching) {
      setVesselROS(vessel?.ros as boolean);
    }
  }, [isVesselFetching, isVesselLoading, vessel?.ros]);

  const jobOrderSearchParams: jobOrderSearchParams = {
    assigneeId: "",
    vesselId: !!id ? id : "",
    port: "",
    rating: undefined,
    compatibleWithRating: undefined,
    jobOrderID: "",
    fillByAfterDate: undefined,
    fillByBeforeDate: undefined,
    completed: undefined,
    notApplicable: undefined,
    completedOrNotApplicable: undefined,
    deleted: false,
    archived: false,
    critical: undefined,
    newJobOrder: undefined,
    modifiedByCaptain: undefined,
    page: 0,
    size: 100,
    sortColumn: "fillByDate",
    sortDirection: "asc",
    publishStatuses: [],
    smis: undefined,
    profileId: undefined,
  };
  const {
    data: jobOrders,
    isLoading: isJobOrdersLoading,
    isFetching: isJobOrdersFetching,
  } = useGetJobOrdersQuery(jobOrderSearchParams);

  const dispatchSearchParams: DispatchSearchParams = {
    vesselId: !!id ? id : "",
    fleetId: undefined,
    marinerIds: [],
    ratings: undefined,
    current: true,
  };
  const {
    data: dispatches,
    isLoading: isDispatchesLoading,
    isFetching: isDispatchesFetching,
  } = useGetDispatchesQuery(dispatchSearchParams);

  const [updateVessel] = useUpdateVesselMutation();
  const [saveJobOrders] = useSaveJobOrdersMutation();
  const [saveDispatches] = useSaveDispatchesMutation();
  const { data: portsFromApi } = useGetPortsQuery();

  const defaultJobOrder = useCallback(() => {
    return {
      createdDate: "",
      fillByDate: "",
      smis: "",
      vessel:
        id !== undefined
          ? {
              id: parseInt(id),
              name: undefined,
              fleet: undefined,
              crewRequirements: undefined,
              rosRequirements: undefined,
              ros: undefined,
              ports: undefined,
            }
          : undefined,
      assignee: undefined,
      onsigner: undefined,
      offsigner: undefined,
      onsignerOnboarded: undefined,
      onsignerLeavingFrom: "",
      onsignerDestination: "",
      onsignerTravelTimestamp: "",
      onsignerAccomodation: "",
      onsignerPickupDate: "",
      onsignerDropoffDate: "",
      onsignerNotes: "",
      onsignerUnionHall: "",
      offsignerDeboarded: false,
      offsignerLeavingFrom: "",
      offsignerDestination: "",
      offsignerTravelTimestamp: "",
      offsignerAccomodation: "",
      offsignerPickupDate: "",
      offsignerDropoffDate: "",
      offsignerNotes: "",
      checklistRoot: undefined,
      travelDocuments: [],
      rating: JobRating._Master,
      port: "",
      reliefReason: undefined,
      flightNotice: undefined,
      reliefTime: undefined,
      covid19Testing: undefined,
      testingLocation: undefined,
      jobType: LicensedJobType.TEMPORARY,
      publish: Publish.CREATED,
      deleted: false,
      note: undefined,
      extra: false,
    };
  }, [id]);

  const convertJobOrdersAndDispatchesToRoster = useCallback(
    (jobOrdersFromApi: JobOrder[], dispatchesFromApi: Dispatch[]) => {
      let jobOrders: any[] = JSON.parse(JSON.stringify(jobOrdersFromApi));
      let dispatches: any[] = JSON.parse(JSON.stringify(dispatchesFromApi));
      let jobOrdersWithOffsignerMap: Map<number, JobOrder> = new Map<
        number,
        JobOrder
      >();
      let jobOrdersWithoutOffsigner: Array<JobOrder> = new Array<JobOrder>();

      jobOrders.forEach((order: JobOrder) => {
        if (order.fillByDate != null) {
          order.fillByDate = convertDateToString(new Date(order.fillByDate));
        }
        if (
          !!order.offsigner &&
          !!order.offsigner.id &&
          order.offsignerDeboarded !== true
        ) {
          jobOrdersWithOffsignerMap.set(order.offsigner.id, order);
        } else {
          jobOrdersWithoutOffsigner.push(order);
        }
      });

      let ratingsCountMap = vesselROS
        ? vessel?.rosRequirements
        : vessel?.crewRequirements;
      if (!!ratingsCountMap) {
        ratingsCountMap = new Map(Object.entries(ratingsCountMap));
      }

      const onsignerRoster = jobOrdersWithoutOffsigner.map(
        (order: JobOrder): RosterMember => {
          if (
            !!ratingsCountMap &&
            ratingsCountMap.has(JobRating[order.rating])
          ) {
            const ratingCount = ratingsCountMap.get(JobRating[order.rating]);
            ratingsCountMap?.set(
              JobRating[order.rating],
              ratingCount == null ? 0 : ratingCount - 1
            );
          }
          return {
            rating: order.rating,
            jobOrder: order,
            initialJobOrder: {
              offsignerDeboarded: order.offsignerDeboarded,
              fillByDate: order.fillByDate,
              port: order.port,
              reliefReason: order.reliefReason,
              offsignerDestination: order.offsignerDestination,
              onsignerOnboarded: order.onsignerOnboarded,
              offsignerNotes: order.offsignerNotes,
              onsignerNotes: order.onsignerNotes,
              extra: order.extra,
            },
            offsigning: true,
            currentDaysOnboard: 0,
            dispatch: {
              startDate: "",
              endDate: "",
              rating: JobRating._Master,
              profileId: undefined,
              profile: undefined,
              vesselId: undefined,
              vessel: undefined,
              dispatchDuration: DispatchDuration.REGULAR,
              extra: undefined,
            },
          };
        }
      );

      const today = moment(new Date());
      const onBoardRoster = dispatches.map(
        (dispatch: Dispatch): RosterMember => {
          var jobOrder: JobOrder;
          var offsigning: boolean;
          dispatch.startDate = convertDateToString(
            new Date(dispatch.startDate)
          );
          const currentDaysOnboard: number =
            Math.ceil(
              today
                .startOf("day")
                .diff(moment(dispatch.startDate).startOf("day"), "days")
            ) + 1;
          if (
            !!dispatch.profileId &&
            jobOrdersWithOffsignerMap.has(dispatch.profileId) &&
            !!jobOrdersWithOffsignerMap.get(dispatch.profileId)
          ) {
            offsigning = true;
            jobOrder = jobOrdersWithOffsignerMap.get(dispatch.profileId!)!;
          } else {
            offsigning = false;
            jobOrder = defaultJobOrder();
          }
          if (
            !!ratingsCountMap &&
            ratingsCountMap.has(JobRating[dispatch.rating])
          ) {
            const ratingCount = ratingsCountMap.get(JobRating[dispatch.rating]);
            ratingsCountMap?.set(
              JobRating[dispatch.rating],
              ratingCount == null ? 0 : ratingCount - 1
            );
          }
          const rosterMember: RosterMember = {
            rating: dispatch.rating,
            dispatch: dispatch,
            jobOrder: jobOrder,
            initialJobOrder: {
              offsignerDeboarded: jobOrder.offsignerDeboarded,
              fillByDate: jobOrder.fillByDate,
              port: jobOrder.port,
              reliefReason: jobOrder.reliefReason,
              offsignerDestination: jobOrder.offsignerDestination,
              onsignerOnboarded: jobOrder.onsignerOnboarded,
              offsignerNotes: jobOrder.offsignerNotes,
              onsignerNotes: jobOrder.onsignerNotes,
              extra: jobOrder.extra,
            },
            offsigning: offsigning,
            currentDaysOnboard: currentDaysOnboard,
          };
          return rosterMember;
        }
      );

      let roster = onsignerRoster.concat(onBoardRoster);

      let pendingRoster: RosterMember[] = [];
      const fillFosRatings = rosUpdated === true && vesselROS === false;
      if (fillFosRatings) {
        const defaultJobOrderValues = defaultJobOrder();
        ratingsCountMap?.forEach((value: number, key: string) => {
          for (let i = 0; i < value; i++) {
            var jobOrder = Object.assign({}, defaultJobOrderValues);
            jobOrder.fillByDate = convertDateToString(new Date());
            jobOrder.rating = JobRating[key as keyof typeof JobRating];
            if (
              vessel?.name === "MARAD Altair" ||
              vessel?.name === "MARAD Bellatrix"
            ) {
              jobOrder.port = "Marrero, LA";
            } else if (
              vessel?.name === "MARAD Denebola" ||
              vessel?.name === "MARAD Antares"
            ) {
              jobOrder.port = "Baltimore, MD";
            } else if (
              vessel?.name === "MARAD Pollux" ||
              vessel?.name === "MARAD Regulus"
            ) {
              jobOrder.port = "Beaumont, TX";
            } else if (
              vessel?.name === "MARAD Pacific Collector" ||
              vessel?.name === "MARAD Pacific Tracker"
            ) {
              jobOrder.port = "Portland, OR";
            } else if (vessel?.ports?.length) {
              jobOrder.port = vessel.ports[0];
            }

            pendingRoster.push({
              rating: jobOrder.rating,
              jobOrder: jobOrder,
              initialJobOrder: {
                offsignerDeboarded: defaultJobOrderValues.offsignerDeboarded,
                fillByDate: defaultJobOrderValues.fillByDate,
                port: defaultJobOrderValues.port,
                reliefReason: defaultJobOrderValues.reliefReason,
                offsignerDestination:
                  defaultJobOrderValues.offsignerDestination,
                onsignerOnboarded: defaultJobOrderValues.onsignerOnboarded,
                offsignerNotes: defaultJobOrderValues.offsignerNotes,
                onsignerNotes: defaultJobOrderValues.onsignerNotes,
              },
              offsigning: true,
              currentDaysOnboard: 0,
              dispatch: {
                startDate: "",
                endDate: "",
                rating: jobOrder.rating,
                profileId: undefined,
                profile: undefined,
                vesselId: undefined,
                vessel: undefined,
                dispatchDuration: DispatchDuration.REGULAR,
                extra: undefined,
              },
            });
          }
        });
        roster = roster.concat(pendingRoster);
      }

      roster = roster.map((row, idx) => {
        row.id = idx;
        return row;
      });

      if (fillFosRatings) {
        roster.forEach((row) => {
          if (
            !!row.id &&
            row.jobOrder.id === undefined &&
            pendingRoster.includes(row)
          ) {
            addChangedRows(row.id, RosterDetailType.JOB_ORDER);
          }
        });
      }
      return roster;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      vesselROS,
      rosUpdated,
      vessel?.rosRequirements,
      vessel?.crewRequirements,
      defaultJobOrder,
    ]
  );

  useEffect(() => {
    (async () => {
      if (
        !isDispatchesLoading &&
        !isDispatchesFetching &&
        !isJobOrdersLoading &&
        !isJobOrdersFetching &&
        !isVesselLoading &&
        !isVesselFetching &&
        !!id
      ) {
        const newRoster = convertJobOrdersAndDispatchesToRoster(
          jobOrders?.content as JobOrder[],
          dispatches as Dispatch[]
        );
        setRoster(newRoster);
        props.rosterStatusCallback(newRoster);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    convertJobOrdersAndDispatchesToRoster,
    isDispatchesLoading,
    isDispatchesFetching,
    isJobOrdersLoading,
    isJobOrdersFetching,
    isVesselLoading,
    isVesselFetching,
    jobOrders,
    dispatches,
  ]);

  useEffect(() => {
    setRosUpdated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (vesselROS) {
      const ratingCount: { [rating: string]: number } = {};
      Object.keys(JobRating).forEach((key) => {
        let ratingsCountMap = vessel?.rosRequirements;
        if (!!ratingsCountMap)
          ratingsCountMap = new Map(Object.entries(ratingsCountMap));

        const c = ratingsCountMap?.get(key);
        if (c !== undefined) {
          ratingCount[key] =
            roster.filter(
              (v) =>
                v.rating === key &&
                v.dispatch &&
                !v.jobOrder?.offsignerDeboarded
            ).length - c;
        } else {
          ratingCount[key] = roster.filter((v) => v.rating === key).length;
        }
      });
      setRosExtra(ratingCount);
    }
  }, [vesselROS, roster, vessel?.rosRequirements]);

  const handleROS = (rosValue: boolean) => {
    setChangeToFOS(false);
    setChangeToROS(false);
    setRosUpdated(true);
    setVesselROS(rosValue);
  };

  const handleRosterUpdate = (
    e: any,
    row: RosterMember,
    rosterDetailType: RosterDetailType,
    value?: any
  ) => {
    const { id } = row;
    const tempRoster = [...roster];
    let idx = tempRoster.findIndex((r) => r.id === id);
    if (idx !== -1) {
      if (value !== undefined) {
        rosterDetailType === RosterDetailType.JOB_ORDER
          ? (tempRoster[idx].jobOrder = {
              ...tempRoster[idx].jobOrder,
              [e.target.name]: value,
            })
          : (tempRoster[idx].dispatch = {
              ...tempRoster[idx].dispatch,
              [e.target.name]: value,
            });
      } else {
        rosterDetailType === RosterDetailType.JOB_ORDER
          ? (tempRoster[idx].jobOrder = {
              ...tempRoster[idx].jobOrder,
              [e.target.name]: e.target.value,
            })
          : (tempRoster[idx].dispatch = {
              ...tempRoster[idx].dispatch,
              [e.target.name]: e.target.value,
            });
      }
      addChangedRows(id as number, rosterDetailType);
    }
    setRoster(tempRoster);
  };

  const addChangedRows = (id: number, rosterDetailType: RosterDetailType) => {
    if (rosterDetailType === RosterDetailType.JOB_ORDER) {
      if (!changedRows.includes(id)) {
        let updatedRows = changedRows;
        updatedRows.push(id);
        setChangedRows(updatedRows);
      }
    } else {
      if (!changedDispatches.includes(id)) {
        let updatedDispatches = changedDispatches;
        updatedDispatches.push(id);
        setChangedDispatches(updatedDispatches);
      }
    }
  };

  const removeChangedRows = (id: number) => {
    if (changedRows.includes(id)) {
      setChangedRows(changedRows.filter((row) => row !== id));
    }
    if (changedDispatches.includes(id)) {
      setChangedDispatches(changedDispatches.filter((row) => row !== id));
    }
  };

  const toggleRowEdit = (row: RosterMember, editable: boolean) => {
    const { id } = row;
    const tempRoster = [...roster];
    let idx = tempRoster.findIndex((r) => r.id === id);
    if (idx !== -1) {
      tempRoster[idx].offsigning = editable;
      if (editable === false) {
        tempRoster[idx].jobOrder = defaultJobOrder();
        removeChangedRows(id as number);
      } else {
        if (
          (!!row.dispatch.dispatchDuration &&
            row.dispatch.dispatchDuration === DispatchDuration.SHORT_TERM) ||
          !!row.dispatch.extra
        ) {
          //Pre-fill date, reason, destination, and estimated relief for short term ratings
          row.jobOrder.fillByDate = convertDateToString(new Date());
          row.jobOrder.reliefReason = ReliefReason.OTHER;
          row.jobOrder.offsignerDestination = "LOCAL";
          row.jobOrder.reliefTime = 1;
          //Default ports for known vessels
          if (
            vessel?.name === "MARLIN Isla Bella" ||
            vessel?.name === "MARLIN Perla Del Caribe"
          ) {
            row.jobOrder.port = "Jacksonville, FL";
          } else if (
            vessel?.name === "ORCA Midnight Sun" ||
            vessel?.name === "ORCA North Star"
          ) {
            row.jobOrder.port = "Tacoma, WA";
          } else if (vessel?.ports?.length) {
            row.jobOrder.port = vessel.ports[0];
          }
        } else if (
          vesselROS &&
          (rosExtra[row.rating] > 0 ||
            [JobRating._Master, JobRating._CM].includes(row.rating) ||
            (!!row.dispatch.profileId && !!row.jobOrder.offsignerDeboarded))
        ) {
          //Pre-fill date, reason, destination, and estimated relief for ROS
          row.jobOrder.fillByDate = convertDateToString(new Date());
          row.jobOrder.reliefReason = ReliefReason.OTHER;
          row.jobOrder.offsignerDestination = "LOCAL";
          row.jobOrder.reliefTime = 1;
          if (
            vessel?.name === "MARAD Altair" ||
            vessel?.name === "MARAD Bellatrix"
          ) {
            row.jobOrder.port = "Marrero, LA";
          } else if (
            vessel?.name === "MARAD Denebola" ||
            vessel?.name === "MARAD Antares"
          ) {
            row.jobOrder.port = "Baltimore, MD";
          } else if (
            vessel?.name === "MARAD Pollux" ||
            vessel?.name === "MARAD Regulus"
          ) {
            row.jobOrder.port = "Beaumont, TX";
          } else if (
            vessel?.name === "MARAD Pacific Collector" ||
            vessel?.name === "MARAD Pacific Tracker"
          ) {
            row.jobOrder.port = "Portland, OR";
          } else if (vessel?.ports?.length) {
            row.jobOrder.port = vessel.ports[0];
          }
        }
        addChangedRows(id as number, RosterDetailType.JOB_ORDER);
      }
    }
    setRoster(tempRoster);
  };

  const getBackground = (
    offsigning: boolean,
    jobOrderId: any,
    rowValue: any,
    isRequired: boolean,
    checkError: boolean
  ) => {
    return offsigning
      ? (rowValue === undefined || rowValue === null || rowValue === "") &&
        isRequired &&
        checkError
        ? "offsigning-error"
        : jobOrderId === undefined
        ? "offsigning-new"
        : "offsigning-saved"
      : "";
  };

  const validateJobOrder = (
    newJobOrder: JobOrder,
    hasDispatchProfile: boolean
  ) => {
    if (newJobOrder.fillByDate === undefined || newJobOrder.fillByDate === "") {
      return false;
    }
    if (
      hasDispatchProfile &&
      (newJobOrder.port === undefined || newJobOrder.port === "")
    ) {
      return false;
    }
    if (
      hasDispatchProfile &&
      (newJobOrder.reliefReason === undefined ||
        newJobOrder.reliefReason === null)
    ) {
      return false;
    }
    if (
      hasDispatchProfile &&
      (newJobOrder.offsignerDestination === undefined ||
        newJobOrder.offsignerDestination === "")
    ) {
      return false;
    }
    if (
      hasDispatchProfile &&
      (newJobOrder.reliefTime === undefined || newJobOrder.reliefTime === null)
    ) {
      return false;
    }
    return true;
  };

  const validateDispatch = (dispatch: Dispatch) => {
    if (dispatch.startDate === undefined || dispatch.startDate === "") {
      return false;
    }
    return true;
  };

  const updateRos = (rosValue: boolean) => {
    if (!rosValue) setChangeToFOS(true);
    if (rosValue) setChangeToROS(true);
  };

  const filterRoster = (roster: RosterMember[]) => {
    // default no filter to show the FULL roster.
    // if showing FULL roster, sort by rating unless a sortColumn overrides
    if (!props.filter || props.filter === RosterFilter.FULL) {
      if (!!sortColumn && !!sortDirection) {
        switch (sortColumn) {
          case "currentDaysOnboard":
            return sortDirection === "asc"
              ? roster.sort(
                  (a, b) => a.currentDaysOnboard - b.currentDaysOnboard
                )
              : roster.sort(
                  (a, b) => b.currentDaysOnboard - a.currentDaysOnboard
                );
          case "daysOnboardAtSignoff":
            return roster.sort((a, b) => {
              const aDays =
                !!a.jobOrder.fillByDate &&
                !!a.dispatch.startDate &&
                moment(a.jobOrder.fillByDate)
                  .startOf("day")
                  .isSameOrAfter(moment(a.dispatch.startDate).startOf("day"))
                  ? Math.ceil(
                      moment(a.jobOrder.fillByDate)
                        .startOf("day")
                        .diff(
                          moment(a.dispatch.startDate).startOf("day"),
                          "days"
                        )
                    ) + 1
                  : 0;
              const bDays =
                !!b.jobOrder.fillByDate &&
                !!b.dispatch.startDate &&
                moment(b.jobOrder.fillByDate)
                  .startOf("day")
                  .isSameOrAfter(moment(b.dispatch.startDate).startOf("day"))
                  ? Math.ceil(
                      moment(b.jobOrder.fillByDate)
                        .startOf("day")
                        .diff(
                          moment(b.dispatch.startDate).startOf("day"),
                          "days"
                        )
                    ) + 1
                  : 0;
              if (aDays < bDays) {
                return sortDirection === "asc" ? -1 : 1;
              } else if (aDays > bDays) {
                return sortDirection === "asc" ? 1 : -1;
              } else {
                return 0;
              }
            });
        }
      } else {
        return roster.sort((a: RosterMember, b: RosterMember) => {
          const reorderedRatings = [
            JobRating._CHC,
            JobRating._2CB,
            JobRating._AC,
            JobRating._SA,
            JobRating._STB,
            JobRating._STW,
          ];
          if (a.rating === b.rating) {
            return +Boolean(b.dispatch.profile) - +Boolean(a.dispatch.profile);
          } else if (
            reorderedRatings.includes(a.rating) !==
            reorderedRatings.includes(b.rating)
          ) {
            return reorderedRatings.includes(a.rating) ? 1 : -1;
          } else {
            return (
              Object.keys(JobRating).indexOf(a.rating) -
              Object.keys(JobRating).indexOf(b.rating)
            );
          }
        });
      }
    } else {
      // else, if showing by FILLED/UNFILLED, sort by fillByDate
      return roster
        .filter((row) => {
          if (props.filter === RosterFilter.FILLED) {
            return !!row.jobOrder.onsigner;
          } else {
            // if props.filter === RosterFilter.UNFILLED
            return !!row.jobOrder.id && !row.jobOrder.onsigner;
          }
        })
        .sort((a: RosterMember, b: RosterMember) => {
          const fillByDateComparison = a.jobOrder.fillByDate.localeCompare(
            b.jobOrder.fillByDate
          );
          if (fillByDateComparison !== 0) {
            return fillByDateComparison;
          } else {
            return (
              Object.keys(JobRating).indexOf(a.rating) -
              Object.keys(JobRating).indexOf(b.rating)
            );
          }
        });
    }
  };

  useEffect(() => {
    if (
      props.filter === RosterFilter.FILLED ||
      props.filter === RosterFilter.UNFILLED
    ) {
      setSortColumn(null);
      setSortDirection(undefined);
    }
    filterRoster(roster);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filter, roster]);

  const handleSubmit = (e: FormEvent) => {
    // if the form has already been submitted, do nothing
    if (props.formSubmitted === true) {
      return;
    }
    props.setFormSubmitted(true);
    props.setEditingExtra(false);
    e.preventDefault();
    const tempRoster = [...roster];
    const rosterWithNewJobOrders = tempRoster
      .filter((r) => {
        return (
          changedRows.includes(r.id as number) &&
          r.offsigning &&
          r.jobOrder !== null
        );
      })
      .map((r) => {
        r.jobOrder.rating = r.rating;
        r.jobOrder.offsigner = r.dispatch.profile;
        if (r.jobOrder.id === undefined || r.jobOrder.id === null) {
          if (!!r.jobOrder.offsigner) {
            r.jobOrder.offsignerLeavingFrom = r.jobOrder.port;
          }
        }
        return r;
      });

    const rosterWithDispatchChanges = tempRoster.filter((r) =>
      changedDispatches.includes(r.id as number)
    );

    let errors: number[] = [];

    for (var i = 0; i < rosterWithNewJobOrders.length; i++) {
      if (
        !validateJobOrder(
          rosterWithNewJobOrders[i].jobOrder,
          !!rosterWithNewJobOrders[i].dispatch.profile
        )
      ) {
        errors.push(rosterWithNewJobOrders[i].id as number);
      }
    }

    for (var j = 0; j < rosterWithDispatchChanges.length; j++) {
      if (!validateDispatch(rosterWithDispatchChanges[j].dispatch)) {
        errors.push(rosterWithDispatchChanges[j].id as number);
      }
    }

    submitForm(
      rosterWithNewJobOrders.map((r) => r.jobOrder),
      rosterWithDispatchChanges.map((r) => r.dispatch),
      errors
    );
  };

  async function submitForm(
    jobOrders: JobOrder[],
    dispatches: Dispatch[],
    errors: number[]
  ) {
    if (vesselROS !== vessel?.ros) {
      let updatedVessel = { ...vessel };
      updatedVessel.ros = vesselROS;
      await updateVessel({
        vesselId: updatedVessel?.id?.toString() as string,
        vessel: updatedVessel,
      });
    }

    if (jobOrders.length === 0 && dispatches.length === 0) {
      props.parentCallback({ hasInfo: true });
    } else if (errors.length > 0) {
      props.parentCallback({ hasError: true, errors });
    } else {
      let saveResult: {
        data: {
          successCount: number;
          failedCount: number;
          additionalError?: string;
        };
        hasData: boolean;
      } = {
        data: { successCount: 0, failedCount: 0 },
        hasData: false,
      };
      if (dispatches.length !== 0) {
        const result = JSON.parse(
          JSON.stringify(await saveDispatches({ dispatches: dispatches }))
        );
        saveResult.data.successCount += result.data.successCount;
        saveResult.data.failedCount += result.data.failedCount;
        saveResult.hasData = true;
      }
      if (jobOrders.length !== 0) {
        const result = JSON.parse(
          JSON.stringify(
            await saveJobOrders({ jobOrders: jobOrders, isPatch: isCaptain })
          )
        );
        saveResult.data.successCount += result.data.successCount;
        saveResult.data.failedCount += result.data.failedCount;
        // if there is an additional message, add it
        saveResult.data.additionalError = result.data.additionalError;
        saveResult.hasData = true;
      }
      props.parentCallback(saveResult);
    }
  }

  usePrompt(
    "Changes will not be saved, would you like to leave?",
    changedRows.length > 0 || changedDispatches.length > 0
  );

  if (
    isDispatchesLoading ||
    isJobOrdersLoading ||
    isVesselLoading ||
    isDispatchesFetching ||
    isJobOrdersFetching ||
    isVesselFetching
  ) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSort = (column: string) => (event: React.MouseEvent<unknown>) => {
    const direction =
      sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(direction);
  };

  const handleEvaluationClick = async (dispatchId?: number, evaluationId?: number) => {
    setEvalClick(true);
    if (!dispatchId) {
      toast.error(<p>Missing dispatch ID for evaluation creation.</p>, {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
      });
      setEvalClick(false);
      return;
    }
    if(!!evaluationId){
      navigate(`/evaluations/${evaluationId}`);
      setEvalClick(false);
      return;
    }
    try {
      const result = await createEvalFromDispatch({ dispatchId }).unwrap();
      navigate(`/evaluations/${result.id}`);
    } catch (error) {
      console.error("Error creating evaluation:", error);
      toast.error(<p>Failed to create evaluation.</p>, {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
      });
    }
    setEvalClick(false);
  };

  return (
    <>
      <form id="crewChangeForm" onSubmit={handleSubmit} />
      {!!vessel &&
        (!props.filter || props.filter === RosterFilter.FULL) &&
        vessel.rosRequirements &&
        Object.keys(vessel.rosRequirements).length > 0 && (
          <div className="checkbox-flex">
            <h5
              className={
                !vesselROS ? "os-active b ft-20 color-blue" : "ft-20 b-500"
              }
            >
              FOS
            </h5>
            <Switch
              className="flex align-items-center switch"
              checked={!!vesselROS}
              onClick={() => {
                if (!!vessel && !!vessel.id) {
                  updateRos(!vesselROS);
                }
              }}
              disabled={!!isCaptain}
              inputProps={{ "aria-label": "controlled" }}
            />
            <h5
              className={
                !!vesselROS ? "os-active b ft-20 color-blue" : "ft-20 b-500"
              }
            >
              ROS
            </h5>
          </div>
        )}
      <TableContainer className="roster-table">
        <Table stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableCell className="rating">
                <div className="rating-inner">Rating</div>
              </TableCell>
              <TableCell className="mariner">
                <div className="mariner-inner">Mariner's Name</div>
              </TableCell>
              {props.editingExtra && <TableCell>Extra</TableCell>}
              {props.editingTrainingPermission && <TableCell>Training</TableCell>}
              <TableCell>Departed</TableCell>
              <TableCell className="off">
                <div className="off-inner">Select</div>
              </TableCell>
              <TableCell className="small-field">Order ID</TableCell>
              <TableCell className="medium-field">Date Joined</TableCell>
              {props.filter === RosterFilter.FULL ? (
                <>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === "currentDaysOnboard"}
                      direction={
                        sortColumn === "currentDaysOnboard"
                          ? sortDirection
                          : "asc"
                      }
                      onClick={handleSort("currentDaysOnboard")}
                    >
                      Current Days Onboard
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === "daysOnboardAtSignoff"}
                      direction={
                        sortColumn === "daysOnboardAtSignoff"
                          ? sortDirection
                          : "asc"
                      }
                      onClick={handleSort("daysOnboardAtSignoff")}
                    >
                      Days Onboard at Signoff
                    </TableSortLabel>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell>Current Days Onboard</TableCell>
                  <TableCell>Days Onboard at Signoff</TableCell>
                </>
              )}
              <TableCell className="medium-field required">
                Crew Change Needed
              </TableCell>
              {isNonArcCaptain && <TableCell> Evaluation Needed </TableCell>}
              <TableCell>SIU Seniority</TableCell>
              <TableCell className="medium-field required">
                Port Relief Requested
              </TableCell>
              <TableCell className="medium-field required">Reason</TableCell>
              <TableCell className="large-field required">
                Destination or CIL
              </TableCell>
              <TableCell className="medium-field required">
                Estimated Relief Time (hours)
              </TableCell>
              <TableCell className="large-field">Known Replacement</TableCell>
              <TableCell>Onboarded</TableCell>
              <TableCell className="xl-field">Offsigner Comments</TableCell>
              <TableCell className="xl-field">Onsigner Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterRoster(roster)!.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="rating left wb-wrap">
                  <div className="rating-inner">
                    <Tooltip
                      title={JobRatingLabel[row.rating]}
                      placement="right"
                    >
                      <span>{row.rating.substring(1)}</span>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell
                  className={`mariner left${
                    !row.dispatch.profile ? " gapped" : ""
                  }`}
                >
                  <div className="mariner-inner flag-cell-st">
                    {!!row.dispatch.profile && !noProfileLink ? (
                      <NavLink
                        className="blue-link table-link"
                        to={`/profiles/${row.dispatch.profile.id}`}
                      >
                        {[
                          row.dispatch.profile.firstName,
                          row.dispatch.profile.middleName,
                          row.dispatch.profile.lastName,
                        ].join(" ")}
                      </NavLink>
                    ) : !!row.dispatch.profile && noProfileLink ? (
                      [
                        row.dispatch.profile.firstName,
                        row.dispatch.profile.middleName,
                        row.dispatch.profile.lastName,
                      ].join(" ")
                    ) : (
                      ""
                    )}
                    {!!row.dispatch.dispatchDuration &&
                      row.dispatch.dispatchDuration ===
                        DispatchDuration.SHORT_TERM && (
                        <div className="st-flag">
                          <Tooltip title={"Short-Term Fill"} placement="top">
                            <span>ST</span>
                          </Tooltip>
                        </div>
                      )}
                    {!!row.dispatch.extra && (
                      <div className="extra-flag">
                        <Tooltip title={"Extra"} placement="top">
                          <span>EX</span>
                        </Tooltip>
                      </div>
                    )}
                    {!!row.dispatch.trainingPermission && (
                      <div className="extra-flag">
                        <Tooltip title={"Training Permission"} placement="top">
                          <span>TP</span>
                        </Tooltip>
                      </div>
                    )}
                    {row.dispatch.profile?.agentUnion ===
                      MarinerUnion.NON_UNION && (
                      <div className="extra-flag">
                        <Tooltip title={"Non-Union"} placement="top">
                          <span>NU</span>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </TableCell>
                {props.editingExtra && (
                  <TableCell>
                    {!!row.dispatch.profile && (
                      <Checkbox
                        name="extra"
                        checked={!!row.dispatch.extra}
                        onChange={(e) =>
                          handleRosterUpdate(
                            e,
                            row,
                            RosterDetailType.DISPATCH,
                            e.target.checked
                          )
                        }
                      />
                    )}
                  </TableCell>
                )}
                {props.editingTrainingPermission && (
                  <TableCell>
                    {!!row.dispatch.profile && (
                      <Checkbox
                        name="trainingPermission"
                        checked={!!row.dispatch.trainingPermission}
                        onChange={(e) =>
                          handleRosterUpdate(
                            e,
                            row,
                            RosterDetailType.DISPATCH,
                            e.target.checked
                          )
                        }
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {((!!row.jobOrder.offsigner &&
                    row.jobOrder.id !== undefined) ||
                    (!!row.dispatch.dispatchDuration &&
                      row.dispatch.dispatchDuration ===
                        DispatchDuration.SHORT_TERM) ||
                    !!row.dispatch.extra ||
                    (!!vesselROS &&
                      !!row.dispatch.profileId &&
                      (rosExtra[row.rating] > 0 ||
                        [JobRating._Master, JobRating._CM].includes(
                          row.rating
                        ))) ||
                    row.jobOrder.offsignerDeboarded) && (
                    <Checkbox
                      name="offsignerDeboarded"
                      checked={row.jobOrder.offsignerDeboarded}
                      onChange={(e) =>
                        handleRosterUpdate(
                          e,
                          row,
                          RosterDetailType.JOB_ORDER,
                          e.target.checked
                        )
                      }
                      disabled={isViewOnly}
                    />
                  )}
                </TableCell>
                <TableCell className="off">
                  <div className="off-inner">
                    <Checkbox
                      name="offsigning"
                      checked={row.offsigning}
                      disabled={isViewOnly || row.jobOrder.id !== undefined}
                      onChange={(e) =>
                        toggleRowEdit(row, e.target.checked ? true : false)
                      }
                    />
                  </div>
                </TableCell>
                <TableCell>
                  {"id" in row.jobOrder && !isCaptain && !isExecutive ? (
                    <Link
                      className="blue-link"
                      to={`/jobOrders/${row.jobOrder.id}`}
                      target="_blank"
                    >
                      {row.jobOrder.id}
                    </Link>
                  ) : "id" in row.jobOrder && (!!isCaptain || isExecutive) ? (
                    <div>{row.jobOrder.id}</div>
                  ) : (
                    <div />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    error={
                      (row.dispatch?.startDate === undefined ||
                        row.dispatch?.startDate === "") &&
                      props.changedRowsWithError.includes(row.id as number)
                    }
                    helperText={
                      (row.dispatch?.startDate === undefined ||
                        row.dispatch?.startDate === "") &&
                      props.changedRowsWithError.includes(row.id as number) &&
                      "Date Joined is required."
                    }
                    name="startDate"
                    value={
                      row.dispatch?.startDate === null
                        ? ""
                        : row.dispatch?.startDate
                    }
                    type="date"
                    disabled={isViewOnly || !isCrewing}
                    onChange={(e) =>
                      handleRosterUpdate(e, row, RosterDetailType.DISPATCH)
                    }
                  />
                </TableCell>
                <TableCell>
                  {!!row.currentDaysOnboard || row.currentDaysOnboard === 0
                    ? row.currentDaysOnboard
                    : ""}
                </TableCell>
                <TableCell>
                  {!!row.jobOrder.fillByDate &&
                  !!row.dispatch.startDate &&
                  moment(row.jobOrder.fillByDate)
                    .startOf("day")
                    .isSameOrAfter(
                      moment(row.dispatch.startDate).startOf("day")
                    )
                    ? Math.ceil(
                        moment(row.jobOrder.fillByDate)
                          .startOf("day")
                          .diff(
                            moment(row.dispatch.startDate).startOf("day"),
                            "days"
                          )
                      ) + 1
                    : ""}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning,
                    row.jobOrder.id,
                    row.jobOrder?.fillByDate,
                    true,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning ? (
                    <TextField
                      error={
                        (row.jobOrder?.fillByDate === undefined ||
                          row.jobOrder?.fillByDate === "") &&
                        props.changedRowsWithError.includes(row.id as number)
                      }
                      helperText={
                        (row.jobOrder?.fillByDate === undefined ||
                          row.jobOrder?.fillByDate === "") &&
                        props.changedRowsWithError.includes(row.id as number) &&
                        "Crew Change Needed is required."
                      }
                      name="fillByDate"
                      value={
                        row.jobOrder?.fillByDate === null
                          ? ""
                          : row.jobOrder?.fillByDate
                      }
                      type="date"
                      disabled={
                        isViewOnly || row.initialJobOrder.offsignerDeboarded
                      }
                      onChange={(e) =>
                        handleRosterUpdate(e, row, RosterDetailType.JOB_ORDER)
                      }
                    />
                  ) : (
                    row.jobOrder?.fillByDate
                  )}
                </TableCell>
                {isNonArcCaptain && (
                  <TableCell>
                    {(() => {
                      const fillByDate = row.jobOrder?.fillByDate;
                      if (!fillByDate) return null;
                      const daysUntilChange = moment(fillByDate).diff(moment(), "days");
                      if (daysUntilChange >= 0 && !!row.dispatch.profile &&
                            ![JobRating._Master, JobRating._CM, JobRating._CE, JobRating._1AE].includes(row.dispatch.rating)) {
                        return (
                          <Button
                            className="blue-link table-link"
                            onClick={() => handleEvaluationClick(row.dispatch.id, row.dispatch.evaluationId)}
                            disabled={evalClick}
                          >
                            {!!row.dispatch.evaluationId ? `View Evaluation`:`Create Evaluation`}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </TableCell>
                )}
                <TableCell>
                  {!!row.dispatch.profile ? row.dispatch.profile.seniority : ""}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning,
                    row.jobOrder.id,
                    row.jobOrder?.port,
                    true,
                    props.changedRowsWithError.includes(row.id as number) &&
                      !!row.dispatch.profile
                  )}
                >
                  {row.offsigning ? (
                    <>
                      <FormControl
                        error={
                          (row.jobOrder?.port === undefined ||
                            row.jobOrder?.port === "") &&
                          !!row.dispatch.profile &&
                          props.changedRowsWithError.includes(row.id as number)
                        }
                      >
                        <Select
                          value={row.jobOrder?.port ? row.jobOrder?.port : ""}
                          name="port"
                          displayEmpty={true}
                          disabled={
                            isViewOnly || row.initialJobOrder.offsignerDeboarded
                          }
                          onChange={(e) =>
                            handleRosterUpdate(
                              e,
                              row,
                              RosterDetailType.JOB_ORDER
                            )
                          }
                        >
                          <MenuItem key="TBD" value="TBD">
                            --TBD--
                          </MenuItem>
                          {!!vessel?.ports &&
                            vessel.ports.map((port) => (
                              <MenuItem key={port} value={port}>
                                {port}
                              </MenuItem>
                            ))}
                          {!!vessel &&
                            (!vessel.ports || vessel.ports.length <= 0) &&
                            portsFromApi
                              ?.filter(
                                (port) =>
                                  !DisabledPorts.some(
                                    (dis) => PortMasterLabel[dis] === port
                                  )
                              )
                              .map((port) => (
                                <MenuItem key={port} value={port}>
                                  {PortMasterLabel[port as PortMasterEnum]}
                                </MenuItem>
                              ))}
                        </Select>
                        {(row.jobOrder?.port === undefined ||
                          row.jobOrder?.port === "") &&
                          props.changedRowsWithError.includes(
                            row.id as number
                          ) &&
                          !!row.dispatch.profile && (
                            <FormHelperText>
                              Port Relief requested is required.
                            </FormHelperText>
                          )}
                      </FormControl>
                    </>
                  ) : (
                    <>{row.jobOrder.port}</>
                  )}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning && !!row.dispatch.profile,
                    row.jobOrder.id,
                    row.jobOrder.reliefReason,
                    true,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning && !!row.dispatch.profile ? (
                    <FormControl
                      error={
                        (row.jobOrder.reliefReason === undefined ||
                          row.jobOrder.reliefReason === null) &&
                        props.changedRowsWithError.includes(row.id as number)
                      }
                    >
                      <Select
                        value={
                          row.jobOrder.reliefReason === null ||
                          row.jobOrder.reliefReason === undefined
                            ? ""
                            : row.jobOrder.reliefReason
                        }
                        name="reliefReason"
                        disabled={
                          isViewOnly || row.initialJobOrder.offsignerDeboarded
                        }
                        onChange={(e) =>
                          handleRosterUpdate(
                            e,
                            row,
                            RosterDetailType.JOB_ORDER,
                            e.target.value as ReliefReason
                          )
                        }
                      >
                        {Object.values(ReliefReason).map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {ReliefReasonLabel[reason as ReliefReason]}
                          </MenuItem>
                        ))}
                      </Select>
                      {(row.jobOrder.reliefReason === undefined ||
                        row.jobOrder.reliefReason === null) &&
                        props.changedRowsWithError.includes(
                          row.id as number
                        ) && (
                          <FormHelperText>Reason is required.</FormHelperText>
                        )}
                    </FormControl>
                  ) : (
                    row.jobOrder?.reliefReason
                  )}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning && !!row.dispatch.profile,
                    row.jobOrder.id,
                    row.jobOrder?.offsignerDestination,
                    true,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning && !!row.dispatch.profile ? (
                    <TextField
                      error={
                        (row.jobOrder?.offsignerDestination === undefined ||
                          row.jobOrder?.offsignerDestination === null ||
                          row.jobOrder?.offsignerDestination === "") &&
                        props.changedRowsWithError.includes(row.id as number)
                      }
                      helperText={
                        (row.jobOrder?.offsignerDestination === undefined ||
                          row.jobOrder?.offsignerDestination === null ||
                          row.jobOrder?.offsignerDestination === "") &&
                        props.changedRowsWithError.includes(row.id as number) &&
                        "Destination or CIL is required."
                      }
                      name="offsignerDestination"
                      value={
                        row.jobOrder?.offsignerDestination === null
                          ? ""
                          : row.jobOrder?.offsignerDestination
                      }
                      type="text"
                      disabled={
                        isViewOnly || row.initialJobOrder.offsignerDeboarded
                      }
                      onChange={(e) =>
                        handleRosterUpdate(e, row, RosterDetailType.JOB_ORDER)
                      }
                    />
                  ) : (
                    row.jobOrder?.offsignerDestination
                  )}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning && !!row.dispatch.profile,
                    row.jobOrder.id,
                    row.jobOrder?.reliefTime,
                    true,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning && !!row.dispatch.profile ? (
                    <TextField
                      error={
                        (row.jobOrder?.reliefTime === undefined ||
                          row.jobOrder?.reliefTime === null) &&
                        props.changedRowsWithError.includes(row.id as number)
                      }
                      helperText={
                        (row.jobOrder?.reliefTime === undefined ||
                          row.jobOrder?.reliefTime === null) &&
                        props.changedRowsWithError.includes(row.id as number) &&
                        "Estimated Relief Time is required."
                      }
                      name="reliefTime"
                      value={
                        row.jobOrder?.reliefTime ||
                        row.jobOrder?.reliefTime === 0
                          ? row.jobOrder.reliefTime
                          : ""
                      }
                      type="number"
                      disabled={
                        isViewOnly || row.initialJobOrder.offsignerDeboarded
                      }
                      onChange={(e) =>
                        handleRosterUpdate(
                          e,
                          row,
                          RosterDetailType.JOB_ORDER,
                          e.target.value ? e.target.value : undefined
                        )
                      }
                    />
                  ) : (
                    row.jobOrder?.reliefTime
                  )}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning &&
                      (!row.dispatch.dispatchDuration ||
                        row.dispatch.dispatchDuration ===
                          DispatchDuration.REGULAR),
                    row.jobOrder.id,
                    undefined,
                    false,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning &&
                  // Captains should be able to select replacement for certain ratings
                  (user?.userGroup as UserGroup) === UserGroup.MARINERS &&
                  !(isCaptain && replaceableRatings.includes(row.rating)) ? (
                    [
                      row.jobOrder?.onsigner?.firstName,
                      row.jobOrder?.onsigner?.middleName,
                      row.jobOrder?.onsigner?.lastName,
                    ].join(" ")
                  ) : !row.offsigning ||
                    (!!row.dispatch.dispatchDuration &&
                      row.dispatch.dispatchDuration ===
                        DispatchDuration.SHORT_TERM) ? (
                    ""
                  ) : (
                    <MarinerProfileAutocomplete
                      id="onsignerAutocomplete"
                      value={
                        row.jobOrder.onsigner ? row.jobOrder.onsigner : null
                      }
                      marinerRating={row.rating ? row.rating : undefined}
                      disabled={isViewOnly || row.jobOrder.onsignerOnboarded}
                      onChange={(profile) =>
                        handleRosterUpdate(
                          { target: { name: "onsigner", value: profile } },
                          row,
                          RosterDetailType.JOB_ORDER
                        )
                      }
                    />
                  )}
                </TableCell>
                <TableCell>
                  {((!!row.jobOrder.offsigner &&
                    row.jobOrder.id !== undefined) ||
                    (!!row.jobOrder?.onsigner &&
                      row.jobOrder.id !== undefined)) && (
                    <Checkbox
                      name="onsignerOnboarded"
                      checked={row.jobOrder.onsignerOnboarded}
                      onChange={(e) =>
                        handleRosterUpdate(
                          e,
                          row,
                          RosterDetailType.JOB_ORDER,
                          e.target.checked
                        )
                      }
                      disabled={
                        isViewOnly ||
                        row.initialJobOrder.onsignerOnboarded === true
                      }
                    />
                  )}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning && !!row.dispatch.profile,
                    row.jobOrder.id,
                    undefined,
                    false,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning && !!row.dispatch.profile ? (
                    isViewOnly ? (
                      <Tooltip
                        title={
                          row.jobOrder.offsignerNotes
                            ? row.jobOrder.offsignerNotes
                            : ""
                        }
                      >
                        <input
                          form="crewChangeForm"
                          name="offsignerNotes"
                          value={
                            row.jobOrder?.offsignerNotes === null
                              ? ""
                              : row.jobOrder?.offsignerNotes
                          }
                          type="text"
                          onChange={(e) =>
                            handleRosterUpdate(
                              e,
                              row,
                              RosterDetailType.JOB_ORDER
                            )
                          }
                          disabled={isViewOnly}
                        />
                      </Tooltip>
                    ) : (
                      <input
                        form="crewChangeForm"
                        name="offsignerNotes"
                        value={
                          row.jobOrder?.offsignerNotes === null
                            ? ""
                            : row.jobOrder?.offsignerNotes
                        }
                        type="text"
                        onChange={(e) =>
                          handleRosterUpdate(e, row, RosterDetailType.JOB_ORDER)
                        }
                        disabled={isViewOnly}
                      />
                    )
                  ) : (
                    row.jobOrder?.offsignerNotes
                  )}
                </TableCell>
                <TableCell
                  className={getBackground(
                    row.offsigning,
                    row.jobOrder.id,
                    undefined,
                    false,
                    props.changedRowsWithError.includes(row.id as number)
                  )}
                >
                  {row.offsigning ? (
                    isViewOnly ? (
                      <Tooltip
                        title={
                          row.jobOrder.onsignerNotes
                            ? row.jobOrder.onsignerNotes
                            : ""
                        }
                      >
                        <input
                          form="crewChangeForm"
                          name="onsignerNotes"
                          value={
                            row.jobOrder?.onsignerNotes === null
                              ? ""
                              : row.jobOrder?.onsignerNotes
                          }
                          type="text"
                          onChange={(e) =>
                            handleRosterUpdate(
                              e,
                              row,
                              RosterDetailType.JOB_ORDER
                            )
                          }
                          disabled={isViewOnly}
                        />
                      </Tooltip>
                    ) : (
                      <input
                        form="crewChangeForm"
                        name="onsignerNotes"
                        value={
                          row.jobOrder?.onsignerNotes === null
                            ? ""
                            : row.jobOrder?.onsignerNotes
                        }
                        type="text"
                        onChange={(e) =>
                          handleRosterUpdate(e, row, RosterDetailType.JOB_ORDER)
                        }
                        disabled={isViewOnly}
                      />
                    )
                  ) : (
                    row.jobOrder?.onsignerNotes
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="roster-help">
        <p className="roster-green">
          Rows with a green background are newly checked. A job order will be
          created once you save your changes.
        </p>
        <p className="roster-blue">
          Rows with a blue background are previously created job orders. You can
          edit them here, but they cannot be unchecked.
        </p>
      </div>
      <Dialog open={changeToFOS}>
        <div className="flex-col mb-2 g-1">
          <p>
            Switching from ROS to FOS will automatically create new Job Orders
            for each of the additional Ratings.
          </p>
          <p>
            Please CONFIRM! that you want to proceed with this major action.
          </p>
        </div>
        <div className="flex my-0 gc-1 align-items-center justify-content-center">
          <Button
            className="blue-button-small"
            onClick={() => handleROS(false)}
          >
            Confirm
          </Button>
          <Button
            className="outline-button-small"
            onClick={() => setChangeToFOS(false)}
          >
            Cancel
          </Button>
        </div>
      </Dialog>
      <Dialog open={changeToROS}>
        <p className="mb-2">
          You are switching to ROS, would you like to continue?
        </p>
        <div className="flex my-0 gc-1 align-items-center justify-content-center">
          <Button className="blue-button-small" onClick={() => handleROS(true)}>
            Confirm
          </Button>
          <Button
            className="outline-button-small"
            onClick={() => setChangeToROS(false)}
          >
            Cancel
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default RosterChangeForm;
